import { normalizeFilterSlugsForApi, useFilterSearch, useUrlFilter, type MappedFilterValues } from "@/composables/filter";
import { getEnumKeyByValue, isStringInEnum } from "@/utilities";
import { searchCollectionItemsElastic, type CollectionItemElastic } from "@/services/collection/item/elastic";
import type { CategoryId } from "@/services/collection/categories";
import { fetchAllCategories } from "@/services/collection/categories";
import type { FilterDefinition, SortDefinition } from "@/services/filter";
import { Sort } from "@/services/marketplace/item";

export const DEFAULT_PER_PAGE = 60;

export const QUERY_FILTER_SLUG = "query";
export const CATEGORY_FILTER_SLUG = "category";
export const SORT_FILTER_SLUG = "sort";

type MarketplaceSearchRequest = {
  page: number;
  perPage: number;
  sellable: boolean;
  filters: MappedFilterValues;
  query?: string;
  categoryId?: CategoryId;
  sort?: Sort;
};

/**
 * Marketplace-specific search composable.
 * Uses useUrlFilter() + useFilterSearch() with domain-specific API calls.
 */
export function useMarketplaceSearch(perPage: number = DEFAULT_PER_PAGE) {
  const { filterValues, page, setFilter, setFilters, setRangeFilter, setPage, resetFilters } = useUrlFilter();

  const apiFilterDefinitions = ref<FilterDefinition[]>([]);
  const sortDefinition = ref<SortDefinition | null>(null);

  const filterDefinitions = apiFilterDefinitions;

  // Computed trigger that combines all reactive dependencies
  const searchTrigger = computed(() => ({
    filterValues: filterValues.value,
    page: page.value,
  }));

  const { items, totalItems, isLoading, error, refetch } = useFilterSearch<CollectionItemElastic, MarketplaceSearchRequest>(searchTrigger, {
    perPage,
    buildRequest: async (filters, currentPage, itemsPerPage) => {
      // Extract query value before mapping (it's not in API definitions)
      const query = typeof filters[QUERY_FILTER_SLUG] === "string" ? filters[QUERY_FILTER_SLUG] : undefined;

      // Extract category value before mapping (it's not in API definitions)
      // Await fetchAllCategories to ensure categories are available (cached after first call)
      const categorySlug = typeof filters[CATEGORY_FILTER_SLUG] === "string" ? filters[CATEGORY_FILTER_SLUG] : undefined;
      let categoryId: CategoryId | undefined;
      if (categorySlug) {
        const categories = await fetchAllCategories();
        categoryId = categories.find((cat) => cat.url === categorySlug)?.id;
      }

      // Extract sort value before mapping (default to DateDesc to match UI default)
      const sortRaw = filters[SORT_FILTER_SLUG];
      const sort = typeof sortRaw === "string" && isStringInEnum(sortRaw, Sort) ? getEnumKeyByValue(sortRaw, Sort) : Sort.MarketplaceListedDateDesc;

      // Remove hardcoded filters before normalizing
      const filtersForMapping = { ...filters };
      delete filtersForMapping[QUERY_FILTER_SLUG];
      delete filtersForMapping[CATEGORY_FILTER_SLUG];
      delete filtersForMapping[SORT_FILTER_SLUG];

      const filtersRemapped = normalizeFilterSlugsForApi(filtersForMapping);

      return {
        page: currentPage,
        perPage: itemsPerPage,
        sellable: true,
        filters: filtersRemapped,
        query: query || undefined,
        categoryId,
        sort,
      };
    },
    executeSearch: async (request, options) => {
      const response = await searchCollectionItemsElastic(request, options);

      apiFilterDefinitions.value = response.parameters;
      // sortDefinition.value = response.sort; @todo Not implemented in API yet
      return {
        items: response.items,
        totalItems: response.totalItems,
      };
    },
  });

  // Compute active filter count based on known filter definitions
  const activeFilterCount = computed(() => {
    let count = 0;

    // Count query filter if set
    const queryValue = filterValues.value[QUERY_FILTER_SLUG];
    if (queryValue && queryValue !== "") {
      count++;
    }

    // Count API filters
    if (apiFilterDefinitions.value.length > 0) {
      const filterFields = new Set(apiFilterDefinitions.value.map((f) => f.slug));

      for (const [key, value] of Object.entries(filterValues.value)) {
        // Skip sort field and hardcoded filters (already counted)
        if (sortDefinition.value && key === sortDefinition.value.field) continue;
        if (key === QUERY_FILTER_SLUG) continue;
        if (key === CATEGORY_FILTER_SLUG) continue;
        if (key === SORT_FILTER_SLUG) continue;

        // Check if this is a known filter field (handle range _min/_max)
        const baseField = key.replace(/_min$|_max$/, "");
        if (filterFields.has(baseField) && value !== undefined) {
          if (Array.isArray(value) ? value.length > 0 : value !== "") {
            count++;
          }
        }
      }
    }

    return count;
  });

  return {
    filterValues,
    page,
    setFilter,
    setFilters,
    setRangeFilter,
    setPage,
    resetFilters,
    items,
    totalItems,
    filterDefinitions,
    sortDefinition,
    isLoading,
    error,
    activeFilterCount,
    refetch,
    perPage,
  };
}
